import React, { useState } from "react";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import * as Formik from "formik";
import * as Yup from "yup";
import { Row, Column } from "helpers/layout";
import { Paper } from "components/Paper";
import { css } from "styled-components/macro";
import { PostTag } from "types/PostTag";
import { ConfirmModal } from "modals/ConfirmModal";
import { Error } from "components/Error";
import { SlugAutoCompleteField } from "./fields/SlugAutoCompleteField";
import { SlugField } from "./fields/SlugField";

type FormValues = Pick<PostTag, "name" | "slug">;
type Callback = () => void;

export function PostTagForm(props: {
  type: "create" | "update";
  initialValues?: FormValues;
  onSubmit: (values: FormValues) => Promise<void>;
  onDelete?: () => Promise<void>;
}) {
  const [error, setError] = useState<string | null>(null);
  const [isDeleting] = useState<boolean>(false);
  const [confirmAction, setConfirmAction] = useState<null | Callback>(null);

  return (
    <Formik.Formik<FormValues>
      initialValues={
        props.initialValues || {
          name: "",
          slug: "",
        }
      }
      validationSchema={Yup.object().shape({
        name: Yup.string().required(),
        slug: Yup.string().required(),
      })}
      onSubmit={async (values: FormValues, { setSubmitting }) => {
        try {
          setSubmitting(true);
          await props.onSubmit(values);
        } catch (error: any) {
          const message =
            error.response && error.response.data
              ? Object.values(error.response.data).join(", ")
              : error.toString();

          setError(message);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting }) => {
        return (
          <Formik.Form>
            <Row>
              <div
                css={css`
                  flex: 1 0 50%;
                  width: 50%;
                `}
              >
                <Column>
                  <Paper>
                    <Formik.FastField
                      name="name"
                      label="Name"
                      type="text"
                      fullWidth
                      component={SlugAutoCompleteField}
                      margin="normal"
                      variant="outlined"
                    />
                    <Formik.FastField
                      name="slug"
                      label="Slug"
                      type="text"
                      fullWidth
                      component={SlugField}
                      margin="normal"
                      variant="outlined"
                    />
                  </Paper>
                </Column>
              </div>
              <div
                css={css`
                  flex: 0 0 280px;
                `}
              >
                <Column>
                  <Paper>
                    <Column>
                      <Row justify="flex-end">
                        {props.onDelete && (
                          <div>
                            <Button
                              fullWidth
                              variant="contained"
                              color="secondary"
                              disabled={isDeleting}
                              onClick={() => {
                                setConfirmAction(() => {
                                  return props.onDelete;
                                });
                              }}
                            >
                              {isDeleting ? (
                                <CircularProgress size={25} />
                              ) : (
                                "Delete"
                              )}
                            </Button>
                            {confirmAction !== null && (
                              <ConfirmModal
                                confirmAction={confirmAction}
                                handleClose={() => {
                                  setConfirmAction(null);
                                }}
                                open={true}
                                label="Please confirm deletion"
                              />
                            )}
                          </div>
                        )}
                        <div>
                          <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? (
                              <CircularProgress size={25} />
                            ) : props.type === "update" ? (
                              "Update"
                            ) : (
                              "Create"
                            )}
                          </Button>
                        </div>
                      </Row>
                      {error && <Error text={error} />}
                    </Column>
                  </Paper>
                </Column>
              </div>
            </Row>
          </Formik.Form>
        );
      }}
    </Formik.Formik>
  );
}
